import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';

/**
 * Formatter for parts per million (PPM) values.
 * Ensures negative values are clamped to zero since PPM cannot be negative.
 * Transforms to specified display units if provided.
 *
 * @param {UnitValue} unitValue - The PPM value and units to format
 * @param {any} unitValue.value - The numeric PPM value
 * @param {string} [unitValue.units] - The current units of the value
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted numeric value (minimum 0)
 *   - unitsLabel: The unit string
 *   - valueUnitsLabel: Combined value and units with proper spacing
 *
 * @example
 * formatPPM({ value: 150, units: 'PPM' })
 * // Returns: { valueLabel: '150', unitsLabel: 'PPM', valueUnitsLabel: '150 PPM' }
 *
 * @example
 * formatPPM({ value: -10, units: 'PPM' })
 * // Returns: { valueLabel: '0', unitsLabel: 'PPM', valueUnitsLabel: '0 PPM' }
 */
export const formatPPM = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);
  let formattedValue = formatNumber(transformed.value);

  if (formattedValue !== null && formattedValue !== undefined && formattedValue < 0) {
    formattedValue = 0;
  }

  return unitValueToFormattedUnitLabels({
    value: formattedValue,
    units: transformed.units,
  });
};
